import React, { useEffect, useLayoutEffect, useRef, useId } from "react";
import { createPortal } from "react-dom";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { RemoveScroll } from "react-remove-scroll";
import { cn } from "../../lib/utils";
import { X } from "lucide-react";
import { useProductSurface } from "./ProductSurfaceContext";

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

const modalStack = [];
let scrollLockCount = 0;
let previousBodyOverflow = "";
let previousBodyPaddingRight = "";
let previousRootInert = false;
let activeCustomModalLayers = 0;
// Shared lifecycle signal for UI rendered outside the application root (for example the
// first-visit cookie notice). Consumers use this to yield while an aria-modal layer owns focus.
export const CUSTOM_MODAL_LAYER_EVENT = "vp360:custom-modal-layers";

const isFocusableVisible = (el) => {
  if (el.closest('[hidden],[aria-hidden="true"]')) return false;
  const style = window.getComputedStyle(el);
  if (style.display === "none" || style.visibility === "hidden") return false;
  return (
    el.offsetWidth > 0 ||
    el.offsetHeight > 0 ||
    el.getClientRects().length > 0 ||
    el === document.activeElement
  );
};

const getFocusable = (root) =>
  Array.from(root.querySelectorAll(FOCUSABLE)).filter(isFocusableVisible);

const isTopModal = (modalInstance) => modalStack[modalStack.length - 1] === modalInstance;

const removeFromStack = (modalInstance) => {
  const index = modalStack.lastIndexOf(modalInstance);
  if (index !== -1) modalStack.splice(index, 1);
};

const lockDocument = () => {
  if (scrollLockCount === 0) {
    const { body, documentElement } = document;
    const root = document.getElementById("root");
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

    previousBodyOverflow = body.style.overflow;
    previousBodyPaddingRight = body.style.paddingRight;
    previousRootInert = root?.hasAttribute("inert") || false;

    body.style.overflow = "hidden"; // lock background scroll
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`; // avoid layout shift
    root?.setAttribute("inert", ""); // confine pointer/SR cursor to the dialog
  }
  scrollLockCount += 1;
};

const unlockDocument = () => {
  if (scrollLockCount === 0) return;
  scrollLockCount -= 1;
  if (scrollLockCount === 0) {
    const root = document.getElementById("root");
    document.body.style.overflow = previousBodyOverflow;
    document.body.style.paddingRight = previousBodyPaddingRight;
    if (!previousRootInert) root?.removeAttribute("inert");
    previousBodyOverflow = "";
    previousBodyPaddingRight = "";
    previousRootInert = false;
  }
};

export function Modal({
  open,
  onClose,
  title,
  children,
  size = "md",
  footer,
  ariaLabel,
  description,
  closeLabel = "Close dialog",
  dismissible = true,
  initialFocusRef,
  layerClassName,
}) {
  const productSurface = useProductSurface();
  const panelRef = useRef(null);
  const bodyRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const initialFocusRefRef = useRef(initialFocusRef);
  const modalInstanceRef = useRef({});
  const titleId = useId();
  const descId = useId();
  const hasTitle = Boolean(title);
  const hasDescription = Boolean(description);

  // Record drawers use Radix's modal focus scope. A lifecycle dialog launched from one is the
  // newer authoritative layer, so announce it before paint; RecordDrawer temporarily yields the
  // overlay and restores the same selected record when the child closes.
  useLayoutEffect(() => {
    if (!open) return undefined;
    activeCustomModalLayers += 1;
    document.dispatchEvent(
      new CustomEvent(CUSTOM_MODAL_LAYER_EVENT, {
        detail: { count: activeCustomModalLayers },
      })
    );
    return () => {
      activeCustomModalLayers = Math.max(0, activeCustomModalLayers - 1);
      document.dispatchEvent(
        new CustomEvent(CUSTOM_MODAL_LAYER_EVENT, {
          detail: { count: activeCustomModalLayers },
        })
      );
    };
  }, [open]);

  // Keep the latest callbacks/refs without re-running the lifecycle effect (avoids focus theft on re-render).
  useEffect(() => {
    onCloseRef.current = onClose;
    initialFocusRefRef.current = initialFocusRef;
  });

  // A11y (UX-001): scroll lock (+ scrollbar compensation), inert background, initial focus into the
  // dialog body, and focus restoration on close. Keyed to `open` only so typing never steals focus.
  useEffect(() => {
    if (!open) return undefined;
    const modalInstance = modalInstanceRef.current;
    const previouslyFocused = document.activeElement;
    const openScrollX = window.scrollX;
    const openScrollY = window.scrollY;
    const restoreOpenScroll = () => {
      if (window.scrollX !== openScrollX || window.scrollY !== openScrollY) {
        window.scrollTo(openScrollX, openScrollY);
      }
    };

    // Keep ancestor launch controls when a child decision closes both dialog layers.
    modalInstance.returnFocusTargets = [previouslyFocused, ...(modalStack.at(-1)?.returnFocusTargets || [])];
    modalStack.push(modalInstance);
    lockDocument();

    // Move focus to the first field in the body (not the Close button), else the panel itself.
    const focusInitial = () => {
      const panel = panelRef.current;
      if (!panel) return;
      // Already focused INSIDE this dialog? Leave it alone. This runs a FRAME after open, so
      // anything that deliberately placed focus in the meantime is more specific than "put the
      // caret somewhere sensible on open" — and yanking it back to the panel is real focus theft,
      // not a nicety: on a busy main thread the frame can land arbitrarily late. Caught by
      // ChatModeration's walk-back, where the last page hands focus to the "Start of this
      // conversation" marker and this callback stole it back to the panel milliseconds later
      // (traced: marker focused at +29ms, panel steal at +43ms). Being a race, it passed on a fast
      // machine and failed consistently on a loaded CI runner.
      if (panel.contains(document.activeElement)) {
        restoreOpenScroll();
        return;
      }
      const requestedInitial = initialFocusRefRef.current?.current;
      const initial =
        requestedInitial instanceof Element &&
        typeof requestedInitial.focus === "function" &&
        panel.contains(requestedInitial) &&
        isFocusableVisible(requestedInitial)
          ? requestedInitial
          : getFocusable(bodyRef.current || panel)[0] || panel;
      initial.focus({ preventScroll: true });
      restoreOpenScroll();
    };
    let settleFrame;
    const frame = window.requestAnimationFrame(() => {
      focusInitial();
      // Chromium may perform a late scroll-anchor adjustment after a fixed portal
      // is inserted. Reassert the locked background position on the next frame.
      settleFrame = window.requestAnimationFrame(restoreOpenScroll);
    });

    return () => {
      window.cancelAnimationFrame(frame);
      if (settleFrame) window.cancelAnimationFrame(settleFrame);
      const wasTop = isTopModal(modalInstance);
      removeFromStack(modalInstance);
      unlockDocument();
      // Restore focus to whatever opened the dialog (only when this was the topmost layer).
      const returnTarget = modalInstance.returnFocusTargets.find(target =>
        target && typeof target.focus === "function" && document.contains(target) && !target.closest?.("[inert]"));
      if (wasTop && returnTarget) {
        returnTarget.focus({ preventScroll: true });
      }
      restoreOpenScroll();
    };
  }, [open]);

  // Keyboard: Escape (when dismissible) + focus trap, scoped to the topmost modal so stacked
  // dialogs don't double-close and Tab is sealed even when focus is on the panel.
  useEffect(() => {
    if (!open) return undefined;
    const modalInstance = modalInstanceRef.current;
    const onKey = (e) => {
      if (!isTopModal(modalInstance)) return;
      const panel = panelRef.current;
      if (!panel) return;

      if (e.key === "Escape") {
        if (dismissible) onCloseRef.current?.();
        return;
      }
      if (e.key !== "Tab") return;

      const nodes = getFocusable(panel);
      if (!nodes.length) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const firstEl = nodes[0];
      const lastEl = nodes[nodes.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && (active === firstEl || active === panel || !panel.contains(active))) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && (active === lastEl || !panel.contains(active))) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, dismissible]);

  if (!open) return null;
  const requestClose = () => {
    if (dismissible) onClose?.();
  };
  const sizes = { sm: "max-w-md", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl" };
  return createPortal(
    // Join Radix's layer stacks so a parent Sheet yields focus and wheel/touch scrolling
    // without remounting its form. Our existing lifecycle still owns initial/return focus.
    <FocusScope asChild trapped onMountAutoFocus={event => event.preventDefault()} onUnmountAutoFocus={event => event.preventDefault()}>
    <RemoveScroll forwardProps removeScrollBar={false} allowPinchZoom>
    <div
      className={cn(
        // Radix Sheet temporarily disables pointer events outside its own layer. Dialogs opened
        // from a record-drawer action are a legitimate nested layer, so explicitly restore pointer
        // events on the newer portal while its own focus trap and backdrop remain authoritative.
        "pointer-events-auto fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto overscroll-contain p-3 sm:p-8",
        layerClassName
      )}
      data-modal-layer="true"
    >
      <div
        className="fixed inset-0 bg-overlay/50 backdrop-blur-sm animate-overlay-in motion-reduce:animate-none motion-reduce:backdrop-blur-none"
        onClick={requestClose}
        aria-hidden="true"
        data-ui="modal-overlay"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={hasTitle ? titleId : undefined}
        aria-label={hasTitle ? undefined : ariaLabel || "Dialog"}
        aria-describedby={hasDescription ? descId : undefined}
        tabIndex={-1}
        className={cn(
          "relative w-full bg-surface rounded-2xl shadow-pop border border-line animate-modal-in motion-reduce:animate-none my-auto flex flex-col max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-4rem)] outline-none",
          sizes[size]
        )}
        data-testid="modal"
        data-ui="modal"
        data-product-surface={productSurface || undefined}
      >
        <div className="flex items-center justify-between gap-4 px-4 py-4 border-b border-line shrink-0 sm:px-6">
          {hasTitle && (
            <h3 id={titleId} className="font-heading font-semibold text-ink text-lg">{title}</h3>
          )}
          {dismissible && (
            <button
              type="button"
              onClick={requestClose}
              aria-label={closeLabel}
              className="ml-auto -mr-2 inline-flex items-center justify-center min-h-11 min-w-11 rounded-lg hover:bg-muted hover:text-ink text-ink-subtle focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface focus-visible:outline-none"
              data-testid="modal-close"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          )}
        </div>
        <div ref={bodyRef} className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-6 sm:py-5" data-modal-body="true" data-ui="modal-body">
          {hasDescription && (
            <div id={descId} className="sr-only">{description}</div>
          )}
          {children}
        </div>
        {footer && (
          <div className="px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-line flex flex-col-reverse gap-3 bg-muted/40 rounded-b-2xl shrink-0 sm:px-6 sm:flex-row sm:flex-wrap sm:justify-end" data-ui="modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
    </RemoveScroll>
    </FocusScope>,
    document.body
  );
}
